import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, Bot, User } from "lucide-react";
import { simulationService } from "@/services/simulationService";
import { filterTrackingPhrases, isOnlyTrackingPhrases } from "@/utils/transcriptFiltering";

interface SimulatorTranscriptProps {
  simulator: {
    id: string;
    name: string;
    status: string;
    transcript?: any[];
    personas?: { name: string };
    assistants?: { name: string };
  };
  open: boolean;
  onClose: () => void;
}

export default function SimulatorTranscript({
  simulator,
  open,
  onClose,
}: SimulatorTranscriptProps) {
  const [transcript, setTranscript] = useState<any[]>(simulator.transcript || []);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isPolling, setIsPolling] = useState(false);

  useEffect(() => {
    // If simulation is running, start polling for updates
    if (simulator.status === "running" && open) {
      setIsPolling(true);
      const pollInterval = setInterval(async () => {
        try {
          const status = await simulationService.getSimulationStatus(simulator.id);
          setTranscript(status.transcript || []);
          
          // Stop polling if simulation is complete
          if (status.status !== "running") {
            setIsPolling(false);
            clearInterval(pollInterval);
          }
        } catch (error) {
          console.error("Error polling transcript:", error);
        }
      }, 2000); // Poll every 2 seconds

      return () => {
        clearInterval(pollInterval);
        setIsPolling(false);
      };
    }
  }, [simulator.status, simulator.id, open]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const status = await simulationService.getSimulationStatus(simulator.id);
      setTranscript(status.transcript || []);
    } catch (error) {
      console.error("Error refreshing transcript:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "bg-gray-100 text-gray-800";
      case "running":
        return "bg-blue-100 text-blue-800";
      case "completed":
        return "bg-green-100 text-green-800";
      case "failed":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatTimestamp = (timestamp: string) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleTimeString();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Simulation Transcript: {simulator.name}</span>
            <div className="flex items-center gap-2">
              <Badge className={getStatusColor(simulator.status)}>
                {simulator.status}
              </Badge>
              {simulator.status === "running" && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                >
                  {isRefreshing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                </Button>
              )}
            </div>
          </DialogTitle>
          <DialogDescription>
            Conversation between{" "}
            <span className="font-medium">{simulator.personas?.name || "Unknown Persona"}</span> and{" "}
            <span className="font-medium">{simulator.assistants?.name || "Unknown Assistant"}</span>
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-[500px] w-full rounded-md border p-4">
          {transcript.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              {simulator.status === "pending" ? (
                <>
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                  <p>Waiting for simulation to start...</p>
                </>
              ) : simulator.status === "running" ? (
                <>
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                  <p>Simulation is running...</p>
                  {isPolling && <p className="text-sm text-muted-foreground mt-2">Auto-refreshing...</p>}
                </>
              ) : (
                <p>No transcript available</p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {transcript.map((entry, index) => {
                // Skip system messages in display (like summary)
                if (entry.role === "system") {
                  return null;
                }

                const isAgent = entry.role === "agent" || entry.role === "assistant";
                const isUser = entry.role === "user" || entry.role === "persona";

                // Skip agent messages that contain only tracking phrases
                if (isAgent && isOnlyTrackingPhrases(entry.content)) {
                  return null;
                }

                // Filter tracking phrases from agent messages
                const displayContent = isAgent ? filterTrackingPhrases(entry.content) : entry.content;

                return (
                  <div
                    key={index}
                    className={`flex gap-3 ${isAgent ? "justify-start" : "justify-end"}`}
                  >
                    <div
                      className={`flex gap-3 max-w-[70%] ${
                        isAgent ? "flex-row" : "flex-row-reverse"
                      }`}
                    >
                      <div
                        className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                          isAgent
                            ? "bg-primary/10 text-primary"
                            : "bg-secondary/50 text-secondary-foreground"
                        }`}
                      >
                        {isAgent ? <Bot className="h-4 w-4" /> : <User className="h-4 w-4" />}
                      </div>
                      <div className="flex flex-col gap-1">
                        <div
                          className={`rounded-lg px-4 py-2 ${
                            isAgent
                              ? "bg-primary/5 text-foreground"
                              : "bg-secondary text-secondary-foreground"
                          }`}
                        >
                          <p className="text-sm">{displayContent}</p>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          {entry.timestamp && (
                            <span>{formatTimestamp(entry.timestamp)}</span>
                          )}
                          {entry.turn && <span>Turn {entry.turn}</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Show summary if available */}
              {transcript.some(t => t.summary) && (
                <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                  <h4 className="font-medium mb-2">Summary</h4>
                  {transcript
                    .filter(t => t.summary)
                    .map((entry, index) => (
                      <div key={index} className="text-sm text-muted-foreground">
                        <p>Total turns: {entry.summary.total_turns}</p>
                        <p>Ended by: {entry.summary.ended_by}</p>
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}